import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Event } from '../../../models/event/event';
import { ROUTES } from '../../../constants/routes';
import EventCard from './EventCard';

interface EventsSectionProps {
  title: string;
  description: string;
  events: Event[];
  showViewAllButton?: boolean;
  viewAllButtonText?: string;
  viewAllButtonLink?: string;
  limit?: number;
  loading?: boolean;
}

const EventsSection: React.FC<EventsSectionProps> = ({
  title,
  description,
  events,
  showViewAllButton = true,
  viewAllButtonText,
  viewAllButtonLink = ROUTES.EVENTS,
  limit,
  loading = false
}) => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';

  const displayedEvents = limit ? events.slice(0, limit) : events;

  // Loading skeleton
  if (loading) {
    return (
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6">
          {/* Header skeleton */}
          <div className="text-center mb-16">
            <div className="w-16 h-16 bg-gray-200 animate-pulse rounded-full mx-auto mb-6"></div>
            <div className="h-12 bg-gray-200 animate-pulse rounded mx-auto mb-4 max-w-md"></div>
            <div className="h-6 bg-gray-200 animate-pulse rounded mx-auto max-w-2xl"></div>
          </div>

          {/* Cards skeleton */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[1, 2, 3].map((i) => (
              <div key={i} className="bg-white rounded-2xl shadow-lg overflow-hidden animate-pulse">
                <div className="h-64 bg-gray-200"></div>
                <div className="p-6">
                  <div className="h-6 bg-gray-200 rounded mb-4"></div>
                  <div className="h-4 bg-gray-200 rounded mb-2"></div>
                  <div className="h-4 bg-gray-200 rounded mb-4 w-3/4"></div>
                  <div className="h-10 bg-gray-200 rounded"></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-20">
      <div className="max-w-7xl mx-auto px-6">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-black text-white rounded-full mb-6">
            <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
          </div>

          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            {title}
          </h2>

          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            {description}
          </p>
        </div>

        {/* Events Grid */}
        {displayedEvents.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
            {displayedEvents.map((event) => (
              <EventCard key={event.id} event={event} />
            ))}
          </div>
        ) : (
          /* Empty State */
          <div className="text-center py-16">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-gray-100 text-gray-400 rounded-full mb-6">
              <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">
              {t('pages.events.noEvents')}
            </h3>
            <p className="text-gray-600">
              {t('pages.events.noEventsDescription')}
            </p>
          </div>
        )}

        {/* View All Button */}
        {showViewAllButton && displayedEvents.length > 0 && (
          <div className="text-center">
            <Link
              to={viewAllButtonLink}
              className="inline-flex items-center justify-center bg-black text-white hover:bg-gray-800 px-8 py-4 rounded-lg transition-all duration-300 font-semibold hover:scale-105 shadow-lg hover:shadow-xl"
            >
              <span>{viewAllButtonText || t('pages.events.viewAllUpcoming')}</span>
              <svg className={`w-5 h-5 ${isRTL ? 'mr-2' : 'ml-2'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={isRTL ? "M15 19l-7-7 7-7" : "M9 5l7 7-7 7"} />
              </svg>
            </Link>
          </div>
        )}
      </div>
    </section>
  );
};

export default EventsSection;
